import {ColorsType, StyleType} from '../../../../utils/theming';
import AlertIcon from '../../Icons/icons/alert';
import CheckIcon from '../../Icons/icons/check';
import ChevronDownIcon from '../../Icons/icons/chevronDown';
import CloseIcon from '../../Icons/icons/close';
import {IconSize} from '../../Icons/icon_types';
import InfoIcon from '../../Icons/icons/info';
import SlashIcon from '../../Icons/icons/slash';
import WarningIcon from '../../Icons/icons/warning';

/* Chip Icons */
export const leadingIconMap: {
  [key: string]: React.FC<{size: IconSize; color: string}>;
} = {
  check: CheckIcon,
  alert: AlertIcon,
  warning: WarningIcon,
  slash: SlashIcon,
};

export const trailingIconMap: {
  [key: string]: React.FC<{size: IconSize; color: string}>;
} = {
  close: CloseIcon,
  chevronDown: ChevronDownIcon,
  info: InfoIcon,
};

/*
 * ---------------
 * Colors:
 * The color, state, and style props will define the background, border, and text colors applied.
 * ----------------
 */

export const createColorMap = (Colors: ColorsType) => ({
  white: {
    filled: {
      enable: {
        backgroundColor: Colors.neutral100,
        textColor: Colors.neutral1000,
      },
      hover: {
        backgroundColor: Colors.neutral200,
        textColor: Colors.neutral1000,
      },
      active: {
        backgroundColor: Colors.neutral100,
        textColor: Colors.primary700,
      },
      disable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral500,
      },
    },
    outline: {
      enable: {textColor: Colors.neutral100},
      hover: {textColor: Colors.neutral200},
      active: {
        textColor: Colors.primary700,
        bordersColor: Colors.neutral200,
      },
      disable: {textColor: Colors.neutral300},
    },
  },
  purple: {
    filled: {
      enable: {
        backgroundColor: Colors.primary200,
        textColor: Colors.primary700,
      },
      hover: {
        backgroundColor: Colors.primary400,
        textColor: Colors.primary700,
      },
      active: {
        backgroundColor: Colors.primary200,
        textColor: Colors.primary700,
      },
      disable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral500,
      },
    },
    outline: {
      enable: {textColor: Colors.primary700},
      hover: {textColor: Colors.primary700},
      active: {textColor: Colors.primary700},
      disable: {textColor: Colors.neutral300},
    },
  },
  red: {
    filled: {
      enable: {
        backgroundColor: Colors.alert300,
        textColor: Colors.neutral100,
      },
      hover: {
        backgroundColor: Colors.alert400,
        textColor: Colors.neutral100,
      },
      active: {
        backgroundColor: Colors.alert300,
        textColor: Colors.neutral100,
      },
      disable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral500,
      },
    },
    outline: {
      enable: {textColor: Colors.alert300},
      hover: {textColor: Colors.alert400},
      active: {textColor: Colors.alert400},
      disable: {textColor: Colors.neutral300},
    },
  },
  green: {
    filled: {
      enable: {
        backgroundColor: Colors.success300,
        textColor: Colors.neutral100,
      },
      hover: {
        backgroundColor: Colors.success400,
        textColor: Colors.neutral100,
      },
      active: {
        backgroundColor: Colors.success300,
        textColor: Colors.success300,
      },
      disable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral500,
      },
    },
    outline: {
      enable: {textColor: Colors.success300},
      hover: {textColor: Colors.success400},
      active: {textColor: Colors.success400},
      disable: {textColor: Colors.neutral300},
    },
  },
  yellow: {
    filled: {
      enable: {
        backgroundColor: Colors.warning300,
        textColor: Colors.neutral1000,
      },
      hover: {
        backgroundColor: Colors.warning400,
        textColor: Colors.neutral1000,
      },
      active: {
        backgroundColor: Colors.warning300,
        textColor: Colors.neutral1000,
      },
      disable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral500,
      },
    },
    outline: {
      enable: {textColor: Colors.neutral1000},
      hover: {textColor: Colors.neutral1000},
      active: {textColor: Colors.neutral1000},
      disable: {textColor: Colors.neutral300},
    },
  },
  lightYellow: {
    filled: {
      enable: {
        backgroundColor: Colors.warning100,
        textColor: Colors.warning400,
      },
      hover: {
        backgroundColor: Colors.warning200,
        textColor: Colors.warning400,
      },
      active: {
        backgroundColor: Colors.warning100,
        textColor: Colors.warning400,
      },
      disable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral500,
      },
    },
    outline: {
      enable: {textColor: Colors.warning400},
      hover: {textColor: Colors.warning400},
      active: {textColor: Colors.warning400},
      disable: {textColor: Colors.neutral300},
    },
  },
  blue: {
    filled: {
      enable: {
        backgroundColor: Colors.info300,
        textColor: Colors.neutral100,
      },
      hover: {
        backgroundColor: Colors.info400,
        textColor: Colors.neutral100,
      },
      active: {
        backgroundColor: Colors.info300,
        textColor: Colors.neutral100,
      },
      disable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral500,
      },
    },
    outline: {
      enable: {textColor: Colors.info300},
      hover: {textColor: Colors.info400},
      active: {textColor: Colors.info400},
      disable: {textColor: Colors.neutral300},
    },
  },
  teal: {
    filled: {
      enable: {
        backgroundColor: Colors.secondary600,
        textColor: Colors.neutral100,
      },
      hover: {
        backgroundColor: Colors.secondary800,
        textColor: Colors.neutral100,
      },
      active: {
        backgroundColor: Colors.secondary600,
        textColor: Colors.neutral100,
      },
      disable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral500,
      },
    },
    outline: {
      enable: {textColor: Colors.secondary600},
      hover: {textColor: Colors.secondary800},
      active: {textColor: Colors.secondary800},
      disable: {textColor: Colors.neutral300},
    },
  },
  pink: {
    filled: {
      enable: {
        backgroundColor: Colors.tertiary600,
        textColor: Colors.neutral100,
      },
      hover: {
        backgroundColor: Colors.tertiary800,
        textColor: Colors.neutral100,
      },
      active: {
        backgroundColor: Colors.tertiary600,
        textColor: Colors.neutral100,
      },
      disable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral500,
      },
    },
    outline: {
      enable: {textColor: Colors.tertiary600},
      hover: {textColor: Colors.tertiary900},
      active: {textColor: Colors.tertiary900},
      disable: {textColor: Colors.neutral300},
    },
  },
  grey: {
    filled: {
      enable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral700,
      },
      hover: {
        backgroundColor: Colors.neutral400,
        textColor: Colors.neutral700,
      },
      active: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.primary700,
      },
      disable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral500,
      },
    },
    outline: {
      enable: {textColor: Colors.neutral400},
      hover: {textColor: Colors.neutral600},
      active: {
        textColor: Colors.primary700,
        bordersColor: Colors.neutral400,
      },
      disable: {textColor: Colors.neutral300},
    },
  },
  black: {
    filled: {
      enable: {
        backgroundColor: Colors.neutral1000,
        textColor: Colors.neutral100,
      },
      hover: {
        backgroundColor: Colors.neutral800,
        textColor: Colors.neutral100,
      },
      active: {
        backgroundColor: Colors.neutral1000,
        textColor: Colors.neutral100,
      },
      disable: {
        backgroundColor: Colors.neutral300,
        textColor: Colors.neutral500,
      },
    },
    outline: {
      enable: {textColor: Colors.neutral400},
      hover: {textColor: Colors.neutral600},
      active: {
        textColor: Colors.primary700,
        bordersColor: Colors.neutral400,
      },
      disable: {textColor: Colors.neutral300},
    },
  },
});

/* Chip container styles */
export const getChipGeneralStyles = (
  colors: ColorsType,
  color: string,
  style: string,
  state: string,
  form: string,
  chipSize: string,
) => {
  const dynamicColorMap = createColorMap(colors);

  // @ts-ignore
  const {backgroundColor, textColor, bordersColor} =
    // @ts-ignore
    dynamicColorMap[color]?.[style]?.[state] || {};

  const borderColor =
    style === 'outline' ? bordersColor || textColor : 'transparent';
  const borderWidth = style === 'outline' ? 1 : 0;

  const borderRadius = form === 'round' ? 20 : 4;

  return {
    backgroundColor: backgroundColor || 'transparent',
    borderColor,
    borderWidth,
    borderRadius,
    textColor,
    padding: chipPadding[chipSize as keyof typeof chipPadding],
  };
};

/* Chip text styles */
export const getChipFontStyles = (
  FontStyles: StyleType,
  chipSize: string,
  state: string,
) => {
  switch (chipSize) {
    case 'large':
      return state === 'active'
        ? FontStyles['bodyBoldSM']
        : FontStyles['buttonSM'];
    case 'medium':
      return state === 'active'
        ? FontStyles['bodyBoldXS']
        : FontStyles['bodyRegularXS'];
    case 'small':
      return FontStyles['bodyBoldCap'];
    default:
      return state === 'active'
        ? FontStyles['bodyBoldSM']
        : FontStyles['buttonSM'];
  }
};

export enum chipPadding {
  'large' = '4px 8px',
  'medium' = '3px 6px',
  'small' = '2px 5px',
}

export const getChipTextStyles = (
  fontStyles: StyleType,
  colors: ColorsType,
  color: string,
  style: string,
  state: string,
  chipSize: string,
) => {
  const dynamicColorMap = createColorMap(colors);
  // @ts-ignore
  const textColor = dynamicColorMap[color]?.[style]?.[state]?.textColor || '';
  const chipGeneralFontStyle = getChipFontStyles(fontStyles, chipSize, state);
  const lineHeight =
    chipSize === 'large' ? 18 : chipGeneralFontStyle?.lineHeight;
  const chipFontStyle = {
    ...chipGeneralFontStyle,
    lineHeight: lineHeight,
    color: textColor,
  };

  return chipFontStyle;
};
